// Storage utilities for DigitalOcean FinOps Dashboard

const DATA_TYPES = ['invoices', 'csvLineItems', 'processedData'];
const STALE_AFTER_HOURS = 24;
const REDUCED_ITEM_LIMIT = 1000;

const storageKey = (accountId, dataType) => `${accountId}_${dataType}`;

// Line items repeat the same dozen keys on every row. Storing them as a
// column list plus value arrays roughly halves the space they need, which
// keeps far more accounts inside the browser's storage quota.
const packRows = (rows) => {
  const columns = [];
  const seen = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });
  return {
    packed: true,
    columns,
    rows: rows.map((row) => columns.map((column) => (row[column] === undefined ? null : row[column])))
  };
};

const unpackRows = (packedData) =>
  packedData.rows.map((values) => {
    const row = {};
    packedData.columns.forEach((column, index) => {
      row[column] = values[index];
    });
    return row;
  });

const isPacked = (data) =>
  Boolean(data) && data.packed === true && Array.isArray(data.columns) && Array.isArray(data.rows);

// Keep only the most recent items. Used as a last resort when the full
// dataset does not fit; the result is always flagged as reduced.
const reduceDataSize = (data) => {
  if (!Array.isArray(data) || data.length <= REDUCED_ITEM_LIMIT) return data;
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.date || a.created_at || 0);
    const dateB = new Date(b.date || b.created_at || 0);
    return dateB - dateA; // Most recent first
  });
  return sortedData.slice(0, REDUCED_ITEM_LIMIT);
};

const isQuotaError = (error) =>
  Boolean(error) && (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error.code === 22);

const writeItem = (accountId, dataType, data, meta) => {
  const shouldPack = dataType === 'csvLineItems' && Array.isArray(data);
  const storageItem = {
    ...meta,
    data: shouldPack ? packRows(data) : data,
    lastUpdated: new Date().toISOString(),
    accountId: accountId
  };
  localStorage.setItem(storageKey(accountId, dataType), JSON.stringify(storageItem));
};

// Save data with metadata. `meta` is stored next to the data (for example the
// time range that processed data was calculated for).
export const saveData = (accountId, dataType, data, meta = {}) => {
  try {
    writeItem(accountId, dataType, data, meta);
    return true;
  } catch (error) {
    if (isQuotaError(error) && dataType === 'csvLineItems' && Array.isArray(data)) {
      // The complete dataset does not fit. Keep a recent subset as an offline
      // fallback and flag it, so it is never mistaken for the full data.
      try {
        const reduced = reduceDataSize(data);
        localStorage.removeItem(storageKey(accountId, dataType));
        writeItem(accountId, dataType, reduced, {
          ...meta,
          isReduced: reduced.length < data.length,
          totalCount: data.length
        });
        return true;
      } catch (retryError) {
        console.error('Failed to save even with reduced data:', retryError);
      }
    } else {
      console.error(`Error saving ${dataType} data:`, error);
    }
    return false;
  }
};

// Load data from storage
export const loadData = (accountId, dataType) => {
  try {
    const item = localStorage.getItem(storageKey(accountId, dataType));
    if (!item) return null;

    const parsed = JSON.parse(item);
    if (isPacked(parsed.data)) {
      parsed.data = unpackRows(parsed.data);
    }
    return parsed;
  } catch (error) {
    console.error(`Error loading ${dataType} data:`, error);
    return null;
  }
};

// Describe an already-loaded cache entry for display and refresh decisions.
export const describeCacheEntry = (entry) => {
  if (!entry) return { isCached: false };

  const lastUpdated = new Date(entry.lastUpdated);
  const hoursSinceUpdate = (new Date() - lastUpdated) / (1000 * 60 * 60);
  const hasValidDate = !isNaN(hoursSinceUpdate);

  return {
    isCached: true,
    lastUpdated: lastUpdated,
    hoursSinceUpdate: hasValidDate ? hoursSinceUpdate : Infinity,
    formattedDate: hasValidDate ? lastUpdated.toLocaleString() : 'an unknown time',
    isStale: !hasValidDate || hoursSinceUpdate > STALE_AFTER_HOURS,
    itemCount: Array.isArray(entry.data) ? entry.data.length : 0,
    isReduced: entry.isReduced || false,
    totalCount: entry.totalCount
  };
};

// Check if data needs refresh (older than 24 hours by default)
export const needsRefresh = (accountId, dataType, hoursThreshold = STALE_AFTER_HOURS) => {
  const item = loadData(accountId, dataType);
  if (!item) return true;

  const hoursDifference = (new Date() - new Date(item.lastUpdated)) / (1000 * 60 * 60);
  // Refresh if we can't determine freshness
  return isNaN(hoursDifference) || hoursDifference > hoursThreshold;
};

// Clear all data for an account
export const clearAccountData = (accountId) => {
  try {
    DATA_TYPES.forEach((type) => {
      localStorage.removeItem(storageKey(accountId, type));
    });
    return true;
  } catch (error) {
    console.error('Error clearing account data:', error);
    return false;
  }
};

// Get cache status information for UI display
export const getCacheStatus = (accountId) => {
  try {
    return describeCacheEntry(loadData(accountId, 'csvLineItems'));
  } catch (error) {
    console.error('Error getting cache status:', error);
    return { isCached: false, error: error.message };
  }
};
